/**
 * 「강의」 메뉴 (/syllabi · /syllabi/:syllabusId) — 진도별 문제 탐색 작업 화면.
 *
 * 설계: docs/learning-composition-v2/FE-20260912-syllabus-workspace-design.md (와이어프레임 W01~W08).
 * 계약: docs/learning-composition-v2/BE-20260911-reply-syllabus-preset.md (dev 20cb46d).
 *
 * 강의에 들어오면 강의 정보가 아니라 **마지막 작업 행(없으면 첫 조건 행)** 을 연다. 왼쪽은 진도 나무(이름 + 조건 요약),
 * 오른쪽은 고른 행의 조건과 실제 문제. 행을 고르고 → 필요한 조건만 조절하고 → 같은 화면에서 문제를 본다.
 * 공유·이름·사본·지우기는 나무 머리의 작은 버튼으로 옮겼다(설계 §W05).
 *
 * 나무는 손으로 목차를 치는 자리다 — 이름을 치고 Enter 면 곧바로 저장되고, 새 줄은 Enter 로 연달아 만든다
 * (오너 결정 2026-09-13). 조건만 행별 초안이고 [저장]으로 서버에 간다(useSyllabusWorkspace).
 *
 * 화면 폭(콘텐츠 폭을 재서 가른다 — 앱 사이드바가 폭을 먹으므로 창 폭이 아니다, 설계 §W08):
 *   1080px 이상 — 나무와 작업 칸을 나란히. 나무 칸 폭은 끌어서 조절(시험지 폴더 칸과 같은 손잡이).
 *   768~1079px  — 나무는 [진도 목록] 서랍. 행을 고르면 닫힌다.
 *   767px 이하   — 「진도 목록」과 「문제 보기」를 한 칸씩 바꿔 본다. 개념·라벨 고르기는 하단 시트.
 */
import {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus, AlertTriangle, Users, Search, BookMarked, ArrowLeft, ListTree, X,
} from 'lucide-react';
import { BypassLayout, Button } from '../components/common';
import V2DateTime from '../components/common/V2DateTime';
import { useAuth } from '../context/AuthContext';
import SyllabusLeavePrompt from '../components/SyllabusV2/SyllabusLeavePrompt';
import SyllabusTreeDrawer from '../components/SyllabusV2/SyllabusTreeDrawer';
import DraftRecoveryPanel from '../components/SyllabusV2/DraftRecoveryPanel';
import SyllabusTree from '../components/SyllabusV2/SyllabusTree';
import TreeLevelControls from '../components/SyllabusV2/TreeLevelControls';
import SyllabusHeader from '../components/SyllabusV2/SyllabusHeader';
import WorkPanel from '../components/SyllabusV2/WorkPanel';
import { shareLabel } from '../components/SyllabusV2/SharePopover';
import useSyllabusWorkspace from '../components/SyllabusV2/useSyllabusWorkspace';
import { readLabelNames, rememberLabelNames } from '../components/SyllabusV2/LabelPicker';
import { readOpenIds, writeOpenIds } from '../components/SyllabusV2/openState';
import { expandTo, descendantsOf } from '../components/SyllabusV2/treeIndex';
import { summarizeExpr, searchTextOfExpr, newExpr } from '../components/SyllabusV2/quickExpr';
import { makeNames } from '../components/SyllabusV2/names';
import {
  TREE_WIDTH, clampTreeWidth, readTreeWidth, writeTreeWidth,
} from '../components/SyllabusV2/syllabusPaneWidth';
import { listSyllabi, createSyllabus, describeSyllabusError } from '../components/SyllabusV2/syllabusApi';
import { fetchConceptDictV2 } from '../components/ExamPapersV2/conceptBadge';

const SCOPE_TABS = [['all', '전체'], ['mine', '내 강의'], ['shared', '공유받은 강의']];
const LIST_PER_PAGE = 50;
const WIDE_MIN = 1080;
const RECENT_KEY = 'syllabus-recent';

const panelCls = 'flex h-full min-h-0 flex-col rounded-3xl bg-white p-4 shadow-lg';
const tabCls = (on) => `whitespace-nowrap px-3 py-1 text-xs transition-colors ${on ? 'bg-primary font-medium text-white' : 'text-gray-500 hover:bg-gray-200'}`;

function readRecent() {
  try { return JSON.parse(window.sessionStorage.getItem(RECENT_KEY)); } catch (e) { return null; }
}
function writeRecent(v) {
  try { window.sessionStorage.setItem(RECENT_KEY, JSON.stringify(v)); } catch (e) { /* 곁들이는 상태 */ }
}

/** 강의 목록(주소에 강의 번호가 없을 때) — 범위·이름 찾기·쪽 이동. 첫 100개 이후도 닿는다(설계 §6) */
function SyllabusListPanel({ onOpen, getAccessToken }) {
  const [scope, setScope] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [state, setState] = useState({
    loading: true, items: [], totalPages: 1, totalItems: 0, error: null,
  });
  const [creating, setCreating] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = useRef(0);

  const load = useCallback(async () => {
    gen.current += 1;
    const g = gen.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const d = await listSyllabi({
        scope, q: q.trim(), page, perPage: LIST_PER_PAGE,
      }, getAccessToken);
      if (g === gen.current) {
        setState({
          loading: false, items: d.items, totalPages: d.totalPages, totalItems: d.totalItems, error: null,
        });
      }
    } catch (e) {
      if (g === gen.current) setState((s) => ({ ...s, loading: false, error: describeSyllabusError(e) }));
    }
  }, [scope, q, page, getAccessToken]);

  useEffect(() => {
    if (!q) { load(); return undefined; }
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load, q]);

  // 범위·검색어가 바뀌면 첫 쪽부터
  useEffect(() => { setPage(1); }, [scope, q]);

  const submitCreate = async (ev) => {
    ev.preventDefault();
    const name = (creating || '').trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const d = await createSyllabus({ name }, getAccessToken);
      setCreating(null);
      onOpen(d.syllabus_id);
    } catch (e) {
      setState((s) => ({ ...s, error: describeSyllabusError(e) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={panelCls}>
      <div className="mb-3 flex items-center gap-2 border-b pb-2">
        <BookMarked className="size-4 shrink-0 text-primary" />
        <h2 className="text-sm font-bold text-gray-900">강의</h2>
        <Button size="xs" className="ml-auto" onClick={() => setCreating('')} disabled={creating != null}>
          <Plus className="mr-0.5 size-3" /> 새 강의
        </Button>
      </div>
      {creating != null && (
        <form onSubmit={submitCreate} className="mb-2 flex items-center gap-1">
          <input
            // [새 강의]를 눌러 연 입력이라 초점이 따라와야 한다
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            type="text"
            value={creating}
            onChange={(e) => setCreating(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setCreating(null); }}
            maxLength={120}
            placeholder="강의 이름"
            aria-label="새 강의 이름"
            className="min-w-0 flex-1 rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
          />
          <Button size="xs" type="submit" loading={busy} disabled={!creating.trim()}>만들기</Button>
          <Button size="xs" variant="secondary" onClick={() => setCreating(null)} disabled={busy}>취소</Button>
        </form>
      )}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-full bg-gray-100" role="tablist" aria-label="강의 범위">
          {SCOPE_TABS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={scope === k} onClick={() => setScope(k)} className={tabCls(scope === k)}>{label}</button>
          ))}
        </div>
        <label className="relative min-w-32 flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} aria-label="강의 이름 검색" placeholder="이름으로 찾기" className="w-full rounded-lg border py-1 pl-7 pr-2 text-xs focus:border-primary focus:outline-none" />
        </label>
      </div>
      {state.error && (
        <div role="alert" className="mb-2 flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{state.error}</span>
          <button type="button" onClick={load} className="shrink-0 underline hover:text-red-700">다시 시도</button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {state.loading && !state.items.length && <p className="px-2 py-6 text-center text-xs text-gray-400">강의를 불러오는 중…</p>}
        {!state.loading && !state.error && !state.items.length && (
          <p className="px-2 py-6 text-center text-xs text-gray-400">
            {scope === 'shared' ? '공유받은 강의가 없습니다.' : '강의가 없습니다 — [새 강의]로 만드세요.'}
          </p>
        )}
        <ul className="space-y-1">
          {state.items.map((s) => (
            <li key={s.syllabus_id}>
              <button type="button" onClick={() => onOpen(s.syllabus_id)} className="w-full rounded-xl px-3 py-2 text-left transition-colors hover:bg-gray-50">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900" title={s.name}>{s.name}</span>
                  {!s.is_mine && <Users aria-label="공유받은 강의" className="size-3.5 shrink-0 text-gray-400" />}
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">{shareLabel(s.share_scope)}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-gray-400">
                  <span>행 {s.node_count ?? 0}개</span>
                  {!s.is_mine && s.author_name && <span>{s.author_name}</span>}
                  <V2DateTime value={s.updated_at} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {state.totalPages > 1 && (
        <div className="mt-2 flex items-center justify-center gap-3 border-t pt-2 text-xs text-gray-500">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || state.loading} className="rounded-full px-2 py-1 hover:bg-gray-100 disabled:opacity-40">이전</button>
          <span>{page} / {state.totalPages}</span>
          <button type="button" onClick={() => setPage((p) => Math.min(state.totalPages, p + 1))} disabled={page >= state.totalPages || state.loading} className="rounded-full px-2 py-1 hover:bg-gray-100 disabled:opacity-40">다음</button>
        </div>
      )}
    </div>
  );
}

/** 목록 화면의 오른쪽 — 빈 안내 대신 「이어서 작업」(설계 감사 §2-1) */
function RecentPanel({ onOpen }) {
  const recent = useMemo(() => readRecent(), []);
  return (
    <div className={`${panelCls} items-center justify-center text-center`}>
      {recent?.id != null ? (
        <>
          <p className="text-xs text-gray-500">최근 작업</p>
          <p className="mt-1 max-w-full truncate text-base font-bold text-gray-900" title={recent.name}>{recent.name}</p>
          <Button size="sm" className="mt-3" onClick={() => onOpen(recent.id)}>이어서 작업</Button>
        </>
      ) : (
        <p className="text-sm text-gray-400">왼쪽에서 강의를 고르거나 [새 강의]로 만드세요.</p>
      )}
    </div>
  );
}

function Syllabus() {
  const { syllabusId } = useParams();
  const navigate = useNavigate();
  const { getAccessToken, userId } = useAuth();
  const ws = useSyllabusWorkspace(syllabusId ?? null, { getAccessToken });

  const [dict, setDict] = useState(null);
  const [labelNames, setLabelNames] = useState(() => readLabelNames());
  const [expanded, setExpanded] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [newRow, setNewRow] = useState(null);
  const [notice, setNotice] = useState(null);
  const [renameRequest, setRenameRequest] = useState(null);
  const [moveRequest, setMoveRequest] = useState(null);
  const [treeWidth, setTreeWidth] = useState(() => readTreeWidth(userId));
  const [resizing, setResizing] = useState(false);
  const [contentWidth, setContentWidth] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pane, setPane] = useState('tree'); // 좁은 화면 — 'tree' | 'work'
  const dragRef = useRef(null);
  const drawerOpenerRef = useRef(null);
  const scrollMemory = useRef(new Map());

  // 개념 사전 — 이름·순번·유형의 재료(모듈 캐시 5분이라 다른 화면과 나눠 쓴다)
  useEffect(() => {
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((d) => { if (alive) setDict(d); })
      .catch(() => { /* 사전이 없으면 개념이 번호로 보일 뿐 — 편집은 막지 않는다 */ });
    return () => { alive = false; };
  }, [getAccessToken]);

  const rememberLabels = useCallback((pairs) => {
    rememberLabelNames(pairs);
    setLabelNames(readLabelNames());
  }, []);
  const names = useMemo(() => makeNames(dict, labelNames), [dict, labelNames]);

  // 강의가 바뀌면 보기 상태를 그 강의 것으로
  useEffect(() => {
    setExpanded(syllabusId != null ? readOpenIds(syllabusId) : new Set());
    setFilter('');
    setNewRow(null);
    setNotice(null);
    setDrawerOpen(false);
    setPane('tree');
  }, [syllabusId]);

  useEffect(() => {
    if (ws.data) writeRecent({ id: ws.data.syllabus_id, name: ws.data.name });
  }, [ws.data?.syllabus_id, ws.data?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // 고른 행이 보이게 조상을 연다(사용자가 연 다른 가지는 그대로)
  const { selectedId, index } = ws;
  useEffect(() => {
    if (selectedId == null || syllabusId == null) return;
    setExpanded((cur) => {
      const next = expandTo(index, selectedId, cur);
      if (next.size === cur.size) return cur;
      writeOpenIds(syllabusId, next);
      return next;
    });
  }, [selectedId, index, syllabusId]);

  // 좁은 화면 — 강의를 열어 행이 골라지면 곧바로 문제 보기로
  const autoPaneRef = useRef(null);
  useEffect(() => {
    if (selectedId != null && autoPaneRef.current !== syllabusId) {
      autoPaneRef.current = syllabusId;
      setPane('work');
    }
  }, [selectedId, syllabusId]);

  // 저장 안 한 조건이 있으면 창을 닫거나 새로고침할 때 묻는다
  useEffect(() => {
    if (!ws.hasUnsaved) return undefined;
    const onBefore = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [ws.hasUnsaved]);

  const openSyllabus = (id) => {
    if (String(id) === String(syllabusId)) return;
    navigate(`/syllabi/${id}`);
  };

  const setOpen = (id, open) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (open) next.add(String(id)); else next.delete(String(id));
      if (syllabusId != null) writeOpenIds(syllabusId, next);
      return next;
    });
  };

  // 머리의 [− N단 +]·전체 펼치기가 고른 집합을 통째로 — 행 하나를 여닫는 setOpen 과 같은 세션 칸에 둔다
  const applyExpanded = (next) => {
    setExpanded(next);
    if (syllabusId != null) writeOpenIds(syllabusId, next);
  };

  const selectRow = (item) => {
    if (!item) return;
    const id = String(item.node_id);
    ws.select(id);
    if (ws.index.children.get(id)?.length) setOpen(id, true);
    setDrawerOpen(false);
    setPane('work');
  };

  const openNewRow = (next) => {
    // 찾기로 걸러진 나무에서는 입력칸이 설 자리가 안 보일 수 있다 — 새 행을 치기 시작하면 찾기를 푼다
    if (next) setFilter('');
    if (next?.parentId != null) setOpen(next.parentId, true);
    setNewRow(next);
  };

  const fail = (e) => { if (e?.reason !== 'stale') setNotice(describeSyllabusError(e)); };

  const createRow = async ({ parentId, afterId, name }) => {
    const r = await ws.create({ parentId, afterId, name });
    if (parentId != null) setOpen(parentId, true);
    return r;
  };

  /** 조건행 만들기 — 행을 곧바로 만들고(제목 줄로 생긴다) 빈 조건 초안을 얹어 고른 뒤 이름 칸을 연다 */
  const createConditionRow = async (parentId, afterId) => {
    try {
      const r = await ws.create({ parentId, afterId, name: '새 조건행' });
      if (parentId != null) setOpen(parentId, true);
      ws.select(r.nodeId, { auto: false });
      ws.setDraft(r.nodeId, newExpr());
      setRenameRequest(r.nodeId);
      setPane('work');
    } catch (e) { fail(e); }
  };

  const deleteRow = (x) => {
    const id = String(x?.node_id ?? x);
    const it = ws.index.byId.get(id);
    const n = descendantsOf(ws.index, id).length;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`「${it?.name}」${n ? `와 하위 행 ${n}개` : ''}를 지울까요? 되돌릴 수 없습니다.`)) return;
    ws.remove(id).catch(fail);
  };

  const convertRow = (x) => {
    const id = String(x?.node_id ?? x);
    const it = ws.index.byId.get(id);
    if (!it) return;
    // 방향은 **서버 식**으로 가른다 — 나무·행 관리 메뉴의 항목 이름이 서버 식으로 정해지므로, 초안이 얹힌 제목 행에서
    // 「이 행에 조건 만들기」를 다시 눌러도 조건을 떼는 확인이 뜨지 않게
    if (it.query == null) {
      // 제목 줄 → 조건 줄: 빈 조건 초안(저장 전까지 서버는 제목 줄 그대로). 초안이 이미 있으면 그대로 연다
      ws.select(id, { auto: false });
      if (ws.queryOf(id) == null) ws.setDraft(id, newExpr());
      setPane('work');
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`「${it.name}」의 조건을 떼고 제목행으로 바꿀까요? 저장된 조건이 사라집니다.`)) return;
    ws.makeTitleRow(id).catch(fail);
  };

  const reparentRow = (x, parentId) => {
    const id = String(x?.node_id ?? x);
    ws.reparent(id, parentId).catch(fail);
  };

  const requestMove = (id) => {
    ws.select(id, { auto: false });
    setMoveRequest(String(id));
    setPane('work');
  };

  // ── 나무 칸 폭(시험지 폴더 칸과 같은 손잡이 — ExamPapers.js) ──
  const commitWidth = (w) => { const next = clampTreeWidth(w); setTreeWidth(next); writeTreeWidth(userId, next); };
  const onResizeDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startW: treeWidth, last: null };
    setResizing(true);
  };
  const onResizeMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const w = clampTreeWidth(d.startW + (e.clientX - d.startX));
    d.last = w;
    setTreeWidth(w);
  };
  const onResizeEnd = (e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setResizing(false);
    if (d.last != null) writeTreeWidth(userId, d.last);
  };
  const onResizeKey = (e) => {
    const moves = {
      ArrowLeft: treeWidth - TREE_WIDTH.step, ArrowRight: treeWidth + TREE_WIDTH.step, Home: TREE_WIDTH.min, End: TREE_WIDTH.max,
    };
    if (!(e.key in moves)) return;
    e.preventDefault();
    commitWidth(moves[e.key]);
  };

  // 콘텐츠 폭 — 앱 사이드바를 뺀 폭으로 가른다(설계 §W08). jsdom 처럼 잴 수 없으면 넓은 화면으로 본다
  // 행을 끌 때 뜨는 「최상위로」 띠를 붙일 상자 — 찾기 칸부터 나무까지(renderTreePanel). SyllabusTree 가 이 상자에 포털로 붙인다
  const treeBoxRef = useRef(null);
  const observerRef = useRef(null);
  const measureRef = useCallback((el) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContentWidth(Math.round(w));
    });
    ro.observe(el);
    observerRef.current = ro;
  }, []);
  // 끊기는 이 콜백 ref 가 null 을 받을 때(요소가 빠질 때) 한다. 따로 effect 치우기로 끊으면 개발 모드(StrictMode)가
  // effect 를 걷었다 다시 세울 때 감시만 끊기고 ref 는 다시 불리지 않아, 폭이 바뀌어도 서랍으로 바뀌지 않는다.

  const selected = ws.selectedId != null ? ws.index.byId.get(ws.selectedId) || null : null;
  const summaryOf = useCallback((item) => {
    const q = ws.queryOf(item.node_id);
    return q == null ? '' : summarizeExpr(q, names);
  }, [ws, names]); // eslint-disable-line react-hooks/exhaustive-deps
  const conflictBar = ws.conflict && ws.conflict.nodeId == null && (
    <div role="alert" className="mb-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
      <span className="min-w-0 flex-1">{ws.conflict.message}</span>
      <button type="button" onClick={() => ws.resolveConflict()} className="shrink-0 underline">확인</button>
    </div>
  );

  const renderTreePanel = ({ sheet = false, onClose = null } = {}) => (
    <div className={panelCls}>
      <div className="mb-2 flex items-start gap-1">
        <div className="min-w-0 flex-1">
          <SyllabusHeader
            data={ws.data}
            owner={ws.owner}
            busy={ws.pendingWrites > 0}
            onBack={() => navigate('/syllabi')}
            onSwitch={openSyllabus}
            // 새 강의는 이미 만들어졌지만, 여는 것은 저장 안 한 조건을 잃는 이동이라 같은 확인을 거친다
            onCreated={(id) => navigate(`/syllabi/${id}`)}
            sheet={sheet}
            onShare={ws.share}
            onUpdateHeader={ws.updateHeader}
            onCopy={({ withDraft }) => ws.copy({ withDraftOf: withDraft ? ws.selectedId : null })}
            copyRecovery={ws.copyRecovery}
            onRetryCopyRecovery={ws.retryCopyRecovery}
            onDiscardCopyRecovery={ws.discardCopyRecovery}
            onDelete={async () => { await ws.removeSyllabus(); navigate('/syllabi'); }}
            canCopyDraft={selected != null && ws.isDirty(selected.node_id)}
            getAccessToken={getAccessToken}
          />
        </div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="진도 목록 닫기" className="shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><X className="size-4" /></button>
        )}
      </div>
      {/* 찾기 칸부터 나무까지 한 상자 — 행을 끌 때 뜨는 「최상위로」 띠(SyllabusTree)가 이 상자를 기준으로 **찾기 칸 위**를 덮는다.
          폴더 나무는 그 띠로 [전체]·[없음](원래 받지 않는 줄)을 덮는데, 강의 나무에는 그런 줄이 없어 첫 행 대신
          드롭 대상이 아닌 찾기 칸을 덮는다(오너 결정 2026-09-13). 기준이 스크롤 상자 밖이어야 띠가 내용과 함께 흘러가지 않는다.
          띠는 나무 안이 아니라 **이 상자에 포털로** 붙는다 — 전역 CSS 가 모든 요소에 preserve-3d 를 걸어 absolute 가 바로 위 부모를
          기준으로 서기 때문이다(SyllabusTree 띠 주석). 그래서 ref 를 넘긴다. */}
      <div ref={treeBoxRef} className="relative flex min-h-0 flex-1 flex-col">
      {/* 찾기 칸과 펼침 단계 한 줄(오너 C안 2026-11-02). 돋보기는 **입력칸 뒤에** 적는다 — 전역 preserve-3d 로 앞에 두면 입력칸의 흰 바탕이 덮는다
          (SyllabusTree 새 행 칸과 같은 까닭). 칸은 남는 폭을 쓰고 단계 버튼은 제 폭을 지킨다. */}
      <div className="mb-2 flex items-center gap-1.5">
        <label className="relative block min-w-0 flex-1">
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="진도 행 찾기" placeholder="진도 행 찾기 — 이름·개념·라벨" className="h-7 w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-7 pr-2 text-xs focus:border-primary focus:outline-none" />
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
        </label>
        <TreeLevelControls index={ws.index} expanded={expanded} onChange={applyExpanded} disabled={filter.trim() !== ''} />
      </div>
      {conflictBar}
      {notice && (
        <div role="alert" className="mb-2 flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="알림 닫기" className="shrink-0"><X className="size-3.5" /></button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <SyllabusTree
          items={ws.items}
          selectedId={ws.selectedId}
          expanded={expanded}
          onToggle={setOpen}
          onSelect={selectRow}
          mode="manage"
          readOnly={!ws.owner}
          summaryOf={summaryOf}
          dirtyIds={ws.dirtyIds}
          filterText={filter}
          extraText={(id) => searchTextOfExpr(ws.queryOf(id), names)}
          onRename={(id, name) => ws.rename(id, name)}
          newRow={newRow}
          onNewRowChange={openNewRow}
          onCreate={createRow}
          onRetryOrder={(nodeId, afterId) => ws.placeAfter(nodeId, afterId)}
          onMove={(id, dir) => ws.move(id, dir).catch(fail)}
          onReparentRequest={requestMove}
          onDelete={deleteRow}
          onConvert={convertRow}
          onMoveTo={(id, dest) => ws.moveTo(id, dest)}
          rootDropHost={treeBoxRef}
          busy={ws.pendingWrites > 0}
          emptyText={ws.owner ? '행이 없습니다 — 아래 [+ 최상위 행 추가]로 첫 행을 치세요.' : '행이 없습니다.'}
        />
      </div>
      </div>
      {sheet && <p className="mt-2 text-[11px] text-gray-400">행을 고르면 문제 보기로 돌아갑니다.</p>}
    </div>
  );

  const renderWork = ({ sheet = false, topSlot = null } = {}) => (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <DraftRecoveryPanel ws={ws} onOpenCopy={openSyllabus} dict={dict} labelNames={labelNames} />
      <div className="min-h-0 flex-1">
        <WorkPanel
          scrollMemory={scrollMemory.current}
          ws={ws}
          node={selected}
          dict={dict}
          labelNames={labelNames}
          onRememberLabels={rememberLabels}
          onStartNewRow={openNewRow}
          onCreateConditionRow={createConditionRow}
          onReparent={reparentRow}
          onConvert={convertRow}
          onDelete={deleteRow}
          renameRequest={renameRequest}
          onRenameRequestDone={() => setRenameRequest(null)}
          moveRequest={moveRequest}
          onMoveRequestDone={() => setMoveRequest(null)}
          sheet={sheet}
          topSlot={topSlot}
        />
      </div>
    </div>
  );

  const renderLoadState = () => {
    if (ws.data) return null;
    return (
      <div className={`${panelCls} items-center justify-center text-center`}>
        {ws.error ? (
          <div role="alert" className="flex max-w-md items-start gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span className="min-w-0 flex-1 text-left">{ws.error}</span>
            <button type="button" onClick={() => ws.reload()} className="shrink-0 underline">다시 시도</button>
          </div>
        ) : <p className="text-xs text-gray-400">강의를 불러오는 중…</p>}
        <button type="button" onClick={() => navigate('/syllabi')} className="mt-3 inline-flex items-center gap-1 text-xs text-gray-500 underline">
          <ArrowLeft className="size-3.5" /> 강의 목록으로
        </button>
      </div>
    );
  };

  const workspace = (isMobileView) => {
    if (!ws.data) return renderLoadState();
    const mode = isMobileView ? 'mobile' : (contentWidth != null && contentWidth < WIDE_MIN ? 'medium' : 'wide');
    if (mode === 'mobile') {
      return pane === 'tree' || !selected ? (
        <div className="flex h-full min-h-0 flex-col gap-2">
          <DraftRecoveryPanel ws={ws} onOpenCopy={openSyllabus} dict={dict} labelNames={labelNames} />
          <div className="min-h-0 flex-1">{renderTreePanel({ sheet: true })}</div>
        </div>
      ) : renderWork({
        sheet: true,
        topSlot: (
          <button type="button" onClick={() => setPane('tree')} className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500">
            <ListTree className="size-3.5" /> 진도 목록
          </button>
        ),
      });
    }
    if (mode === 'medium') {
      return (
        <div className="relative h-full">
          {renderWork({
            topSlot: (
              <button ref={drawerOpenerRef} type="button" onClick={() => setDrawerOpen(true)} aria-expanded={drawerOpen} aria-haspopup="dialog" className="mb-1 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">
                <ListTree className="size-3.5" /> 진도 목록 · {ws.data.name}
              </button>
            ),
          })}
          {drawerOpen && (
            <SyllabusTreeDrawer openerRef={drawerOpenerRef} onClose={() => setDrawerOpen(false)}>
              {renderTreePanel({ onClose: () => setDrawerOpen(false) })}
            </SyllabusTreeDrawer>
          )}
        </div>
      );
    }
    return (
      <div className="flex size-full gap-4">
        <div className="relative h-full shrink-0" style={{ width: treeWidth }}>
          {/* 손잡이 — 칸과 옆 카드 사이의 틈(gap-4 = 16px)에 겹친다. 평소엔 안 보이고, 올리거나 초점이 오면 막대가 뜬다 */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="진도 칸 폭"
            aria-valuemin={TREE_WIDTH.min}
            aria-valuemax={TREE_WIDTH.max}
            aria-valuenow={treeWidth}
            tabIndex={0}
            title="끌어서 폭 조절 · 두 번 눌러 원래대로"
            onPointerDown={onResizeDown}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            onKeyDown={onResizeKey}
            onDoubleClick={() => commitWidth(TREE_WIDTH.initial)}
            className="group absolute inset-y-0 -right-4 z-10 flex w-4 cursor-col-resize touch-none items-center justify-center focus:outline-none"
          >
            <span aria-hidden="true" className={`h-10 w-1 rounded-full transition-colors ${resizing ? 'bg-primary' : 'bg-transparent group-hover:bg-gray-300 group-focus-visible:bg-primary'}`} />
          </div>
          {renderTreePanel()}
        </div>
        <div className="h-full min-w-0 flex-1">{renderWork()}</div>
      </div>
    );
  };

  return (
    <>
      <SyllabusLeavePrompt dirty={ws.hasUnsaved} />
      <BypassLayout
        renderContent={({ isMobileView }) => {
          const body = syllabusId == null
            ? (isMobileView
              ? <SyllabusListPanel onOpen={openSyllabus} getAccessToken={getAccessToken} />
              : (
                <div className="flex size-full gap-4">
                  <div className="h-full shrink-0" style={{ width: 'clamp(360px, 40%, 440px)' }}>
                    <SyllabusListPanel onOpen={openSyllabus} getAccessToken={getAccessToken} />
                  </div>
                  <div className="h-full min-w-0 flex-1"><RecentPanel onOpen={openSyllabus} /></div>
                </div>
              ))
            : workspace(isMobileView);
          if (isMobileView) {
            return (
              <div className="flex flex-1 flex-col" style={{ width: 'calc(100% - 28.8px)', height: 'calc(100% - 28.8px)', margin: '14.4px' }}>
                <div className="min-h-0 flex-1">{body}</div>
              </div>
            );
          }
          return <div ref={measureRef} className="h-[calc(100vh-6rem)] w-full">{body}</div>;
        }}
      />
    </>
  );
}

export default Syllabus;
